import { useEffect, useState, useRef, useCallback, type ReactElement } from "react";
import { usePageTracking } from "@/hooks/use-track";
import { useTutorial } from "@/hooks/use-tutorial";
import { usePageTour } from "@/components/ProductTour";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormSkeleton } from "@/components/loading-skeleton";
import { BrandingDisplay } from "@/components/branding-display";
import { CurrencyInput } from "@/components/currency-input";
import { PercentInput } from "@/components/percent-input";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import {
  Building2,
  Save,
  Plus,
  Check,
  Trash2,
  Upload,
  X,
  FolderOpen,
  Loader2,
  User,
  CreditCard,
  FileText,
  MapPin,
  Clock,
  Shield,
  Tag,
  CheckCircle2,
  Lightbulb,
  Copy,
  Link2,
  AlertCircle,
  Gift,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { readPublicQuoteNameAllowed, PROFILE_TEMPLATE_FIELDS } from "@/components/public-quote-name-field";
import { HelpTip } from "@/components/help-tip";
import { REGIONS, getRegions, getSignupDiscount, type BuilderProfile, type AnalysisProfile } from "@shared/schema";
import { getMarketCurrency, formatMoney } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PLAN_FEATURES: Record<string, string[]> = {
  starter: ["10 Auto quotes per month", "Unlimited Template & Manual quotes", "Professional PDF export", "Cost Library with 31 categories", "Email support"],
  pro: ["25 Auto quotes per month", "Unlimited Template & Manual quotes", "Pricing Intelligence system", "Expert validation pipeline", "Regional UK pricing", "Priority support"],
  team: ["50 Auto quotes per month", "Unlimited Template & Manual quotes", "Everything in Starter", "Learned Adjustments", "Drawing analysis", "Dedicated support"],
};

const PLAN_NAMES: Record<string, string> = {
  starter: "Lite",
  pro: "Starter",
  team: "Pro",
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  starter: "Perfect for sole traders getting started.",
  pro: "For growing businesses wanting more power.",
  team: "For established businesses with high quote volume.",
};

export const profileSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  website: z.string().optional(),
  vatNumber: z.string().optional(),
  market: z.string().default("uk"),
  customTaxRate: z.string().optional(),
  defaultLabourRate: z.string().min(1, "Labour rate is required"),
  labourRateType: z.string().default("day"),
  defaultMarkupPercent: z.string().default("15"),
  defaultOverheadPercent: z.string().default("10"),
  quoteStyle: z.string().default("detailed"),
  region: z.string().default("national"),
  regionUplift: z.string().default("0"),
  quoteValidityDays: z.coerce.number().min(1).default(30),
  brandColor: z.string().optional(),
  logoUrl: z.string().nullable().optional(),
  showCompanyName: z.boolean().default(true),
  publicQuoteNameAllowed: z.boolean().default(false),
  logoSize: z.enum(["small", "medium", "large"]).default("medium"),
  defaultTermsConditions: z.string().optional(),
  defaultPaymentTerms: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

function SubscriptionSection({ user, getSubscriptionBadge, getTrialInfo }: { user: any; getSubscriptionBadge: () => ReactElement; getTrialInfo: () => ReactElement | null }) {
  const { toast } = useToast();
  const currentMarket = user?.market || "uk";
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [codeApplied, setCodeApplied] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<{ tier: string; name: string; amount: number; isUpgrade: boolean } | null>(null);

  const { data: subscription } = useQuery<any>({
    queryKey: ['/api/stripe', 'subscription'],
    queryFn: async () => {
      const res = await fetch('/api/stripe/subscription');
      if (!res.ok) return null;
      return res.json();
    },
  });

  const { data: plans } = useQuery<any[]>({
    queryKey: ['/api/stripe/plans', currentMarket],
    queryFn: async () => {
      const res = await fetch(`/api/stripe/plans?market=${encodeURIComponent(currentMarket)}`, { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/stripe/create-portal');
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
    },
    onError: () => {
      toast({ title: "Error", description: "Could not open subscription management portal", variant: "destructive" });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const res = await apiRequest('POST', '/api/stripe/create-checkout', {
        priceId,
        source: user?.subscriptionStatus === "active" ? "settings" : "trial_settings",
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
      setCheckoutLoading(null);
    },
    onError: () => {
      setCheckoutLoading(null);
      toast({ title: "Error", description: "Could not start checkout", variant: "destructive" });
    },
  });

  const applyCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/referrals/apply", { code: code.trim().toLowerCase() });
      return res.json();
    },
    onSuccess: () => {
      setCodeApplied(true);
      toast({ title: "Referral code applied", description: "Your referrer will earn a reward when you subscribe." });
    },
    onError: (error: any) => {
      let msg = "Invalid or already used referral code.";
      try {
        const raw = error?.message || "";
        const jsonPart = raw.substring(raw.indexOf("{"));
        msg = JSON.parse(jsonPart)?.message || msg;
      } catch {}
      toast({ title: "Could not apply code", description: msg, variant: "destructive" });
    },
  });

  const upgradeMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const res = await apiRequest('POST', '/api/stripe/upgrade', { priceId });
      return res.json();
    },
    onSuccess: () => {
      setCheckoutLoading(null);
      queryClient.invalidateQueries({ queryKey: ['/api/stripe', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({ title: "Plan updated", description: "Your subscription has been changed. Stripe will apply prorated charges automatically." });
    },
    onError: (error: any) => {
      setCheckoutLoading(null);
      let msg = "Could not change plan";
      try { msg = JSON.parse(error?.message?.substring(error?.message?.indexOf("{")))?.message || msg; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const TIER_ORDER: Record<string, number> = { starter: 1, pro: 2, team: 3 };

  const handleSubscribe = (tier: string) => {
    const plan = plans?.find((p: any) => p.tier === tier);
    if (!plan?.priceId) return;
    if (hasActiveSubscription) {
      const currentOrder = TIER_ORDER[currentTier ?? ""] ?? 0;
      const planOrder = TIER_ORDER[tier] ?? 0;
      setPendingPlanChange({
        tier,
        name: plan.name,
        amount: plan.amount,
        isUpgrade: planOrder > currentOrder,
      });
    } else {
      setCheckoutLoading(tier);
      checkoutMutation.mutate(plan.priceId);
    }
  };

  const confirmPlanChange = () => {
    if (!pendingPlanChange) return;
    const plan = plans?.find((p: any) => p.tier === pendingPlanChange.tier);
    if (!plan?.priceId) return;
    setCheckoutLoading(pendingPlanChange.tier);
    setPendingPlanChange(null);
    upgradeMutation.mutate(plan.priceId);
  };

  const { data: referralInfo } = useQuery<{ wasReferred: boolean; referralDiscountApplied: boolean } | null>({
    queryKey: ["/api/referrals/me"],
    queryFn: async () => {
      const res = await fetch("/api/referrals/me");
      if (!res.ok) return null;
      return res.json();
    },
  });

  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
  const currentTier = subscription?.tier;
  const wasReferred = referralInfo?.wasReferred || codeApplied;
  const discountApplied = referralInfo?.referralDiscountApplied ?? false;

  const currentPlanName = plans?.find((p: any) => p.tier === currentTier)?.name ?? currentTier ?? "current plan";
  const discountAmt = formatMoney(parseFloat(getSignupDiscount(currentMarket)), currentMarket);

  return (
    <div className="border-t pt-4">
      <AlertDialog open={!!pendingPlanChange} onOpenChange={(open) => { if (!open) setPendingPlanChange(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingPlanChange?.isUpgrade ? "Upgrade" : "Downgrade"} to {pendingPlanChange?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  You are switching from <span className="font-medium text-foreground capitalize">{currentPlanName}</span> to <span className="font-medium text-foreground">{pendingPlanChange?.name} ({getMarketCurrency(currentMarket).symbol}{pendingPlanChange ? (pendingPlanChange.amount / 100).toFixed(0) : 0}/month + {getMarketCurrency(currentMarket).taxLabel})</span>.
                </p>
                <p>
                  Stripe will automatically calculate a prorated charge or credit for the remainder of your current billing period.
                </p>
                {!pendingPlanChange?.isUpgrade && (
                  <p className="text-amber-600 dark:text-amber-400">
                    Downgrading will reduce your available features straight away.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-plan-change">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPlanChange}
              data-testid="button-confirm-plan-change"
            >
              Confirm {pendingPlanChange?.isUpgrade ? "Upgrade" : "Downgrade"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Subscription</span>
              {getSubscriptionBadge()}
              {currentTier && (
                <Badge variant="outline" data-testid="badge-subscription-tier">
                  {PLAN_NAMES[currentTier] ?? currentTier}
                </Badge>
              )}
            </div>
            {getTrialInfo()}
            {subscription?.currentPeriodEnd && (
              <p className="text-xs text-muted-foreground" data-testid="text-period-end">
                {subscription.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} on {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
          {hasActiveSubscription && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              data-testid="button-manage-subscription"
            >
              {portalMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Manage Subscription'}
            </Button>
          )}
        </div>

        {plans && plans.length > 0 && (
          <>
            {(!hasActiveSubscription || wasReferred) && (
              <div className="flex flex-col items-center gap-4 my-6 py-5 border-t border-b">
                {discountApplied ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted border border-border rounded-lg px-4 py-3 w-full max-w-md" data-testid="banner-referral-discount-used-settings">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>Your {discountAmt} referral discount was applied to a previous subscription.</span>
                  </div>
                ) : wasReferred ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 w-full max-w-md" data-testid="banner-referral-discount-pending-settings">
                    <Gift className="h-4 w-4 shrink-0" />
                    <span><strong>{discountAmt} off your first month</strong> your referral code is saved and will be applied automatically when you subscribe.</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 w-full">
                    <p className="text-sm font-semibold text-foreground text-center">Have a referral code?</p>
                    <p className="text-xs text-muted-foreground text-center -mt-2">Enter a code from a colleague before subscribing to get <strong>{discountAmt} off your first month</strong>.</p>
                    <div className="flex gap-2 w-full max-w-md">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          placeholder="e.g. smith-builds-1234"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && referralCode.trim() && applyCodeMutation.mutate(referralCode)}
                          className="pl-8 text-sm"
                          disabled={applyCodeMutation.isPending}
                          data-testid="input-referral-code-settings"
                        />
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => applyCodeMutation.mutate(referralCode)}
                        disabled={!referralCode.trim() || applyCodeMutation.isPending}
                        data-testid="button-apply-referral-settings"
                      >
                        {applyCodeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {hasActiveSubscription && (
              <p className="text-xs text-muted-foreground pt-1">
                Changing your plan takes effect immediately. Stripe will automatically prorate any difference in price.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              {plans.map((plan: any) => {
                const isPro = plan.tier === "pro";
                const features = PLAN_FEATURES[plan.tier] ?? [];
                const description = PLAN_DESCRIPTIONS[plan.tier] ?? "";
                const isCurrentPlan = currentTier === plan.tier;
                const currentOrder = TIER_ORDER[currentTier ?? ""] ?? 0;
                const planOrder = TIER_ORDER[plan.tier] ?? 0;
                const isUpgrade = hasActiveSubscription && planOrder > currentOrder;
                const isDowngrade = hasActiveSubscription && planOrder < currentOrder;
                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-lg border text-left p-5 space-y-3 ${isCurrentPlan ? "border-primary border-2 shadow-md" : isPro && !hasActiveSubscription ? "border-primary border-2 shadow-md" : ""}`}
                    data-testid={`plan-card-${plan.tier}`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground text-xs font-semibold px-3">Current Plan</Badge>
                      </div>
                    )}
                    {!hasActiveSubscription && isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground text-xs font-semibold px-3">Most Popular</Badge>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{PLAN_NAMES[plan.tier] ?? plan.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                    </div>
                    <div>
                      <span className="text-2xl font-bold">{getMarketCurrency(currentMarket).symbol}{(plan.amount / 100).toFixed(0)}</span>
                      <span className="text-sm text-muted-foreground">/month + {getMarketCurrency(currentMarket).taxLabel}</span>
                    </div>
                    <ul className="space-y-1.5 text-xs text-muted-foreground">
                      {features.map((f) => (
                        <li key={f} className="flex items-start gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      size="sm"
                      variant={isCurrentPlan ? "outline" : (isPro && !hasActiveSubscription) || isUpgrade ? "default" : "outline"}
                      className="w-full"
                      onClick={() => !isCurrentPlan && handleSubscribe(plan.tier)}
                      disabled={isCurrentPlan || checkoutLoading === plan.tier}
                      data-testid={`button-subscribe-${plan.tier}`}
                    >
                      {checkoutLoading === plan.tier
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : isCurrentPlan
                          ? "Current Plan"
                          : isUpgrade
                            ? "Upgrade"
                            : isDowngrade
                              ? "Downgrade"
                              : "Subscribe Now"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function IntegrationsSection() {
  const { toast } = useToast();
  const { data: qbStatus, isLoading } = useQuery<{ configured: boolean; connected: boolean; companyName: string | null; realmId: string | null; needsReconnect: boolean; error: string | null; }>({
    queryKey: ["/api/integrations/quickbooks/status"],
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/integrations/quickbooks");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/integrations/quickbooks/status"] });
      toast({ title: "QuickBooks disconnected" });
    },
    onError: () => {
      toast({ title: "Failed to disconnect QuickBooks", variant: "destructive" });
    }
  });

  const handleConnect = () => {
    window.location.assign("/api/integrations/quickbooks/connect");
  };

  return (
    <Card data-testid="card-integrations">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Link2 className="h-5 w-5 shrink-0" />
          Integrations
        </CardTitle>
        <CardDescription>Connect with other tools you use</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border rounded-lg bg-card">
          <div className="space-y-1">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              QuickBooks Online
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : qbStatus?.connected ? (
                <Badge variant={qbStatus.needsReconnect ? "destructive" : "default"} className="text-[10px] h-5">
                  {qbStatus.needsReconnect ? "Needs Reconnect" : "Connected"}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px] h-5">Disconnected</Badge>
              )}
            </h4>
            <p className="text-sm text-muted-foreground">
              {qbStatus?.companyName
                ? `Connected to ${qbStatus.companyName}`
                : "Create invoices directly from accepted quotes."}
            </p>
            {qbStatus?.error && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {qbStatus.error}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {!isLoading && qbStatus && !qbStatus.configured ? (
              <span className="text-sm text-muted-foreground mr-2" data-testid="text-qb-unconfigured">
                Integration setup required by administrator.
              </span>
            ) : qbStatus?.connected ? (
              <>
                {qbStatus.needsReconnect && (
                  <Button
                    onClick={handleConnect}
                    size="sm"
                    className="w-full sm:w-auto"
                    data-testid="button-reconnect-quickbooks"
                  >
                    Reconnect QuickBooks
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full sm:w-auto text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive" data-testid="button-disconnect-quickbooks">
                      Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect QuickBooks?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will no longer be able to create invoices from your quotes directly. You can reconnect at any time.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => disconnectMutation.mutate()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        data-testid="button-confirm-disconnect-quickbooks"
                      >
                        {disconnectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disconnect"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                size="sm"
                className="w-full sm:w-auto"
                disabled={isLoading}
                data-testid="button-connect-quickbooks"
              >
                Connect QuickBooks
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountSection() {
  const { user } = useAuth();
  if (!user) return null;

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || "No name set";
  const joinedDate = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "Unknown";

  const getSubscriptionBadge = () => {
    switch (user.subscriptionStatus) {
      case "active":
        return <Badge variant="default" data-testid="badge-subscription-active">Active Subscription</Badge>;
      case "trial":
        return <Badge variant="secondary" data-testid="badge-subscription-trial">Free Trial</Badge>;
      case "expired":
        return <Badge variant="outline" data-testid="badge-subscription-expired">Expired</Badge>;
      case "cancelled":
        return <Badge variant="destructive" data-testid="badge-subscription-cancelled">Cancelled</Badge>;
      default:
        return <Badge variant="outline" data-testid="badge-subscription-inactive">Inactive</Badge>;
    }
  };

  const getTrialInfo = () => {
    if (user.subscriptionStatus !== "trial" || !user.trialEndAt) return null;
    const end = new Date(user.trialEndAt);
    const now = new Date();
    const daysLeft = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    const endDateFormatted = end.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-trial-info">
        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
        {daysLeft > 0
          ? <span>{daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining (ends {endDateFormatted})</span>
          : <span>Trial ended on {endDateFormatted}</span>
        }
      </div>
    );
  };

  return (
    <Card data-testid="card-account-info">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <User className="h-5 w-5 shrink-0" />
          Account
        </CardTitle>
        <CardDescription>Your account details and subscription status</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Name</label>
            <p className="text-sm mt-0.5" data-testid="text-account-name">{fullName}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Email</label>
            <p className="text-sm mt-0.5" data-testid="text-account-email">{user.email || "Not set"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Login Method</label>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-sm capitalize" data-testid="text-auth-provider">{user.authProvider || "Email"}</p>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Member Since</label>
            <p className="text-sm mt-0.5" data-testid="text-joined-date">{joinedDate}</p>
          </div>
        </div>

        <SubscriptionSection user={user} getSubscriptionBadge={getSubscriptionBadge} getTrialInfo={getTrialInfo} />
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  usePageTracking("Settings");
  usePageTour("settings");
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = new URL(window.location.href);

    const qbStatus = params.get("quickbooks");
    if (qbStatus === "connected") {
      toast({ title: "QuickBooks Connected", description: "You can now create invoices from accepted quotes." });
      url.searchParams.delete("quickbooks");
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
      queryClient.invalidateQueries({ queryKey: ["/api/integrations/quickbooks/status"] });
    } else if (qbStatus === "error") {
      toast({ title: "QuickBooks Connection Failed", description: "There was a problem connecting to QuickBooks. Please try again.", variant: "destructive" });
      url.searchParams.delete("quickbooks");
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    }

    if (params.get("portal_return") === "1") {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stripe", "subscription"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stripe"] });
      url.searchParams.delete("portal_return");
      window.history.replaceState({}, "", url.toString());
      toast({
        title: "Plan updated",
        description: "Your subscription has been updated successfully.",
        duration: 5000,
      });
    }

  }, [toast]);

  const { data: profile, isLoading } = useQuery<BuilderProfile>({
    queryKey: ["/api/profile"],
  });

  const { user } = useAuth();
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      companyName: "",
      address: "",
      phone: "",
      email: "",
      website: "",
      vatNumber: "",
      market: user?.market || "uk",
      customTaxRate: (user as any)?.customTaxRate || "",
      defaultLabourRate: "250",
      labourRateType: "day",
      defaultMarkupPercent: "15",
      defaultOverheadPercent: "10",
      quoteStyle: "detailed",
      region: "national",
      regionUplift: "0",
      quoteValidityDays: 30,
      brandColor: "#1e40af",
      logoUrl: null,
      showCompanyName: true,
      publicQuoteNameAllowed: false,
      logoSize: "medium",
      defaultTermsConditions: "",
      defaultPaymentTerms: "50% deposit upon instruction, 50% upon completion",
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        companyName: profile.companyName || "",
        address: profile.address || "",
        phone: profile.phone || "",
        email: profile.email || "",
        website: (profile as any).website || "",
        vatNumber: profile.vatNumber || "",
        market: (profile as any).market || user?.market || "uk",
        customTaxRate: (user as any)?.customTaxRate || "",
        defaultLabourRate: String(profile.defaultLabourRate) || "250",
        labourRateType: profile.labourRateType || "day",
        defaultMarkupPercent: String(profile.defaultMarkupPercent) || "15",
        defaultOverheadPercent: String(profile.defaultOverheadPercent) || "10",
        quoteStyle: profile.quoteStyle || "detailed",
        region: profile.region || "national",
        regionUplift: String(profile.regionUplift) || "0",
        quoteValidityDays: profile.quoteValidityDays || 30,
        brandColor: profile.brandColor || "#1e40af",
        logoUrl: profile.logoUrl || null,
        showCompanyName: (profile as any).showCompanyName ?? true,
        publicQuoteNameAllowed: readPublicQuoteNameAllowed(profile),
        logoSize: (profile as any).logoSize || "medium",
        defaultTermsConditions: (profile as any).defaultTermsConditions || "",
        defaultPaymentTerms: (profile as any).defaultPaymentTerms || "50% deposit upon instruction, 50% upon completion",
      });
    }
  }, [profile, form]);

  const saveProfile = async (data: ProfileFormData, successMsg: string) => {
    // Sync market and CA custom tax rate to user record if they changed.
    const userPatch: Record<string, any> = {};
    if (data.market !== user?.market) userPatch.market = data.market;
    if ((data.market === "ca" || data.market === "us") && data.customTaxRate !== undefined && data.customTaxRate !== (user as any)?.customTaxRate) {
      userPatch.customTaxRate = data.customTaxRate;
    }
    if (Object.keys(userPatch).length > 0) {
      await apiRequest("PATCH", "/api/me", userPatch);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    }
    return apiRequest("PUT", "/api/profile", data);
  };

  const currentMarket = form.watch("market") || user?.market || "uk";
  const isAU = currentMarket === "au";
  const isCA = currentMarket === "ca";
  const isUS = currentMarket === "us";

  const AU_REGIONS = [
    { value: "national", label: "National (Average)" },
    { value: "nsw", label: "New South Wales" },
    { value: "vic", label: "Victoria" },
    { value: "qld", label: "Queensland" },
    { value: "wa", label: "Western Australia" },
    { value: "sa", label: "South Australia" },
    { value: "tas", label: "Tasmania" },
    { value: "act", label: "Australian Capital Territory" },
    { value: "nt", label: "Northern Territory" },
  ];

  // US regions are sourced from the canonical schema list (REGIONS_US) so this
  // component stays in lockstep with server-side region uplifts. The schema
  // helper getRegions("us") returns the same array.
  const US_REGIONS = getRegions("us");

  const regionsToUse = isAU ? AU_REGIONS : isUS ? US_REGIONS : REGIONS;

  const quoteMutation = useMutation({
    mutationFn: (data: ProfileFormData) => saveProfile(data, "Quote settings saved"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Quote settings saved" });
    },
    onError: () => {
      toast({ title: "Failed to save quote settings", variant: "destructive" });
    },
  });

  const companyMutation = useMutation({
    mutationFn: (data: ProfileFormData) => saveProfile(data, "Company details saved"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Company details saved" });
    },
    onError: () => {
      toast({ title: "Failed to save company details", variant: "destructive" });
    },
  });

  const selectedRegion = regionsToUse.find(r => r.value === form.watch("region"));

  useEffect(() => {
    if (selectedRegion && selectedRegion.value !== "custom" && "uplift" in selectedRegion) {
      form.setValue("regionUplift", String(selectedRegion.uplift));
    }
  }, [selectedRegion, form]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <FormSkeleton />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-settings-title">
          Settings
          <HelpTip testId="button-help-settings" content="Configure your company details, default quote values, and regional pricing here. These settings are used as defaults whenever you create a new quote. You can also save different configurations as profiles." />
        </h1>
        <p className="text-muted-foreground">Manage your account, business profile, and quote defaults</p>
      </div>

      <AccountSection />

      <IntegrationsSection />


      <Form {...form}>
        {/* Company Details — own save button */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Building2 className="h-5 w-5 shrink-0" />
              Company Details
            </CardTitle>
            <CardDescription>
              This information appears on your branded quotes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Your Building Company Ltd" data-testid="input-company-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Full business address" className="resize-none" data-testid="input-address" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="07700 900000" data-testid="input-phone" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="info@yourcompany.co.uk" data-testid="input-email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="www.yourcompany.co.uk" data-testid="input-website" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="market"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country / Market</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-market-settings">
                          <SelectValue placeholder="Select market" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="uk">United Kingdom 🇬🇧</SelectItem>
                        <SelectItem value="au">Australia 🇦🇺</SelectItem>
                        <SelectItem value="ca">Canada 🇨🇦</SelectItem>
                        <SelectItem value="us">United States 🇺🇸</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="vatNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isAU ? "ABN" : isCA ? "GST/HST Number" : isUS ? "EIN / TIN" : "VAT Number"}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={isAU ? "12 345 678 901" : isCA ? "123456789 RT0001" : isUS ? "12-3456789" : "GB 123 4567 89"} data-testid="input-vat" />
                    </FormControl>
                    <FormDescription>Leave blank if not registered</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {(isCA || isUS) && (
                <FormField
                  control={form.control}
                  name="customTaxRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Combined Sales Tax Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder={isCA ? "e.g. 13.00 (HST ON) or 14.975 (GST+QST QC)" : "e.g. 8.875 (NYC) or 7.25 (LA County)"}
                          data-testid={isCA ? "input-ca-tax-rate" : "input-us-tax-rate"}
                        />
                      </FormControl>
                      <FormDescription>
                        {isCA
                          ? "Enter your combined GST/HST plus PST or QST rate. Used as the default on new quotes; you can override per quote."
                          : "Enter your combined state plus local sales tax rate. Used as the default on new quotes; you can override per quote."}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <LogoUploadField form={form} />
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                disabled={companyMutation.isPending}
                onClick={form.handleSubmit((data) => companyMutation.mutate(data))}
                data-testid="button-save-company"
              >
                <Save className="h-4 w-4 mr-2" />
                {companyMutation.isPending ? "Saving..." : "Save Company Details"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Default Quote Settings + Regional Pricing — own save button */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileText className="h-5 w-5 shrink-0" />
              Default Quote Settings
            </CardTitle>
            <CardDescription>
              These values are used as defaults when creating new quotes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="defaultMarkupPercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Markup %</FormLabel>
                    <FormControl>
                      <PercentInput value={field.value} onChange={field.onChange} data-testid="input-markup" />
                    </FormControl>
                    <FormDescription>Your profit margin</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultOverheadPercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Overhead %</FormLabel>
                    <FormControl>
                      <PercentInput value={field.value} onChange={field.onChange} data-testid="input-overhead" />
                    </FormControl>
                    <FormDescription>Business running costs</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="quoteStyle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quote Style</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-quote-style">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="detailed">Detailed (Full breakdown)</SelectItem>
                      <SelectItem value="simplified">Simplified (Summary only)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>How quotes are presented to clients</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quoteValidityDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quote Validity (days)</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" min="1" max="365" data-testid="input-validity" />
                  </FormControl>
                  <FormDescription>How long quotes remain valid</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultPaymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Payment Terms</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="50% deposit upon instruction, 50% upon completion" data-testid="input-payment-terms" />
                  </FormControl>
                  <FormDescription>Default payment terms for new quotes</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultTermsConditions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Terms & Conditions</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter your default terms and conditions here..."
                      className="min-h-[120px]"
                      data-testid="input-terms-conditions"
                    />
                  </FormControl>
                  <FormDescription>Default terms applied to all new quotes (can be edited per quote)</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Regional Pricing — inside Default Quote Settings */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Regional Pricing</p>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Adjust prices based on your operating region</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="region"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-region">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {regionsToUse.map((region) => (
                            <SelectItem key={region.value} value={region.value} data-testid={`select-region-option-${region.value}`}>
                              {region.label} {(region as any).uplift !== 0 && (region as any).uplift !== undefined && `(${ (region as any).uplift > 0 ? "+" : ""}${ (region as any).uplift }%)`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="regionUplift"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region Uplift %</FormLabel>
                      <FormControl>
                        <PercentInput
                          value={field.value}
                          onChange={field.onChange}
                          disabled={form.watch("region") !== "custom"}
                          data-testid="input-region-uplift"
                        />
                      </FormControl>
                      <FormDescription>
                        {form.watch("region") === "custom"
                          ? "Set your custom regional adjustment"
                          : "Auto-set based on selected region"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                type="button"
                disabled={quoteMutation.isPending}
                onClick={form.handleSubmit((data) => quoteMutation.mutate(data))}
                data-testid="button-save-quote-settings"
              >
                <Save className="h-4 w-4 mr-2" />
                {quoteMutation.isPending ? "Saving..." : "Save Quote Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </Form>

      <SettingsProfilesSection form={form} />

      <TutorialPreferencesSection />

      <DangerZoneSection />
    </div>
  );
}

function DangerZoneSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pendingDeletionRequest = user?.pendingDeletionRequest;

  const requestMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/account/deletion-request", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setConfirmOpen(false);
      toast({
        title: "Deletion requested",
        description: "Your account is queued for review. You can cancel this request at any time before it's processed.",
      });
    },
    onError: () => {
      toast({ title: "Failed to submit deletion request", variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/account/deletion-request/cancel", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Deletion request cancelled" });
    },
    onError: () => {
      toast({ title: "Failed to cancel deletion request", variant: "destructive" });
    },
  });

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-destructive">
          <Trash2 className="h-4 w-4" />
          Danger Zone
        </CardTitle>
        <CardDescription>Permanently delete your account and personal data</CardDescription>
      </CardHeader>
      <CardContent>
        {pendingDeletionRequest ? (
          <div className="flex items-center justify-between gap-3 flex-wrap border rounded-md p-3 bg-destructive/5">
            <div>
              <p className="text-sm font-medium">Deletion request pending review</p>
              <p className="text-xs text-muted-foreground">
                Requested {new Date(pendingDeletionRequest.requestedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.
                An admin will review this before your account is anonymised.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              data-testid="button-cancel-deletion-request"
            >
              {cancelMutation.isPending ? "Cancelling..." : "Cancel Request"}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground max-w-md">
              This will queue your account for deletion. An admin will review the request, then your
              personal data will be anonymised. Financial and subscription records are retained as
              required by law.
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              data-testid="button-delete-account"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete My Account
            </Button>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request account deletion?</AlertDialogTitle>
            <AlertDialogDescription>
              This submits a request to permanently delete your account. Your profile, quotes, and client
              data will be anonymised once an admin confirms the request — this cannot be undone. Financial
              and subscription records are retained for 6 years as required by law. You can cancel this
              request any time before it's processed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-dialog">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                requestMutation.mutate();
              }}
              disabled={requestMutation.isPending}
              data-testid="button-confirm-delete-account"
            >
              {requestMutation.isPending ? "Submitting..." : "Yes, Request Deletion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function TutorialPreferencesSection() {
  const { enabled, globallyOff, enableTips, disableTips, enableTour, disableTour } = useTutorial();

  return (
    <Card data-testid="card-tutorial-preferences">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Lightbulb className="h-5 w-5 shrink-0" />
          Tips &amp; Guidance
        </CardTitle>
        <CardDescription>
          Contextual tips appear throughout the app to help you get the most out of The Proper Tool. Collapse or expand them directly from any tip card.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Tip Cards</p>
            <p className="text-sm text-muted-foreground">
              {!globallyOff
                ? "Turn off all tip cards across the app"
                : "Turn on all tip cards across the app"}
            </p>
          </div>
          <Switch
            checked={!globallyOff}
            onCheckedChange={(val) => val ? enableTips() : disableTips()}
            data-testid="switch-tips-enabled"
            aria-label="Toggle tip cards"
          />
        </div>
        <div className="pt-3 border-t flex items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Guided Tour</p>
            <p className="text-sm text-muted-foreground">
              {enabled
                ? "Guided tour icon is visible in the toolbar"
                : "Turn on to show the guided tour icon in the toolbar"}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(val) => val ? enableTour() : disableTour()}
            data-testid="switch-tour-active"
            aria-label="Toggle guided tour"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function LogoUploadField({ form }: { form: UseFormReturn<ProfileFormData> }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const logoUrl = form.watch("logoUrl");
  const logoSize = form.watch("logoSize");

  const handleUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image must be under 5MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const urlRes = await fetch("/api/uploads/request-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
      });
      if (!urlRes.ok) throw new Error("Failed to get upload URL");
      const { uploadURL, objectPath } = await urlRes.json();

      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Failed to upload file");

      form.setValue("logoUrl", objectPath, { shouldDirty: true });
      toast({ title: "Logo uploaded successfully" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [form, toast]);

  return (
    <div>
      <label className="text-sm font-medium">Company Logo</label>
      <div className="mt-1.5 flex items-center gap-4 flex-wrap">
        {logoUrl ? (
          <div className="relative rounded-md border p-3">
            <BrandingDisplay
              logoUrl={logoUrl}
              companyName=""
              showCompanyName={false}
              logoSize={logoSize}
              sizeClasses={{
                small: "max-h-16 max-w-[200px]",
                medium: "max-h-24 max-w-[280px]",
                large: "max-h-32 max-w-[360px]",
              }}
              imageClassName="rounded-sm"
              imageTestId="img-company-logo"
              nameTestId="text-company-name-preview"
            />
            <button
              type="button"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full border bg-background flex items-center justify-center hover-elevate"
              onClick={() => form.setValue("logoUrl", null, { shouldDirty: true })}
              data-testid="button-remove-logo"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          data-testid="button-upload-logo"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          {uploading ? "Uploading..." : logoUrl ? "Change Logo" : "Upload Logo"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">Appears on your quotes and PDF exports (max 5MB)</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="logoSize"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Logo size on quotes</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="select-logo-size">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>Medium matches the current PDF logo size.</FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="showCompanyName"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
              <div>
                <FormLabel>Show Company Name</FormLabel>
                <FormDescription>
                  Turn off to display only your logo on quotes and exports.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="switch-show-company-name"
                  aria-label="Show Company Name"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function SettingsProfilesSection({ form }: { form: UseFormReturn<ProfileFormData> }) {
  const { toast } = useToast();
  const [newProfileName, setNewProfileName] = useState("");
  const [newProfileDescription, setNewProfileDescription] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: profiles, isLoading, isError } = useQuery<AnalysisProfile[]>({
    queryKey: ["/api/analysis-profiles"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const currentSettings = form.getValues();
      return apiRequest("POST", "/api/analysis-profiles", {
        name: data.name,
        description: data.description || null,
        configPayload: currentSettings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analysis-profiles"] });
      setNewProfileName("");
      setNewProfileDescription("");
      setShowCreateForm(false);
      toast({ title: "Profile saved" });
    },
    onError: () => {
      toast({ title: "Failed to save profile", variant: "destructive" });
    },
  });

  const applyProfile = useCallback((profile: AnalysisProfile) => {
    const config = profile.configPayload as Record<string, any>;
    if (!config) return;

    const fields = PROFILE_TEMPLATE_FIELDS as readonly (keyof ProfileFormData)[];

    fields.forEach((key) => {
      if (key in config) {
        form.setValue(key, config[key], { shouldDirty: true });
      }
    });

    toast({ title: `Profile "${profile.name}" applied`, description: "Settings loaded. Click Save Settings to keep these changes." });
  }, [form, toast]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/analysis-profiles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analysis-profiles"] });
      toast({ title: "Profile deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete profile", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FolderOpen className="h-5 w-5 shrink-0" />
              Settings Profiles
            </CardTitle>
            <CardDescription>
              Save and load different configurations for your business settings
            </CardDescription>
          </div>
          {!showCreateForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreateForm(true)}
              data-testid="button-new-profile"
            >
              <Plus className="h-4 w-4 mr-1" />
              Save Current Profile
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showCreateForm && (
          <div className="space-y-3 border rounded-md p-4">
            <Input
              placeholder="Profile name (e.g. London Premium, Standard Rates)"
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
              data-testid="input-profile-name"
            />
            <Input
              placeholder="Description (optional)"
              value={newProfileDescription}
              onChange={(e) => setNewProfileDescription(e.target.value)}
              data-testid="input-profile-description"
            />
            <div className="flex gap-2 justify-end flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowCreateForm(false); setNewProfileName(""); setNewProfileDescription(""); }}
                data-testid="button-cancel-profile"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!newProfileName.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate({ name: newProfileName.trim(), description: newProfileDescription.trim() })}
                data-testid="button-save-profile"
              >
                <Save className="h-4 w-4 mr-1" />
                {createMutation.isPending ? "Saving..." : "Save Profile"}
              </Button>
            </div>
          </div>
        )}

        {profiles && profiles.length === 0 && !showCreateForm && (
          <div className="text-sm text-muted-foreground">
            No saved profiles yet. Save your current settings as a profile to quickly switch between different configurations.
          </div>
        )}

        {isError ? (
          <div className="text-sm text-destructive">Failed to load profiles. Please try again later.</div>
        ) : isLoading ? (
          <div className="text-sm text-muted-foreground">Loading profiles...</div>
        ) : profiles && profiles.length > 0 ? (
          <div className="space-y-2">
            {profiles.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-2 border rounded-md p-3 flex-wrap"
                data-testid={`profile-row-${p.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2 flex-wrap">
                      {p.name}
                    </div>
                    {p.description && (
                      <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyProfile(p)}
                    data-testid={`button-apply-profile-${p.id}`}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Apply
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => deleteMutation.mutate(p.id)}
                    disabled={deleteMutation.isPending}
                    data-testid={`button-delete-profile-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
